import React, { useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { Container, Row, Col, Form, Button, Card, Badge, Spinner, Modal } from 'react-bootstrap';
import { FaEnvelope } from 'react-icons/fa';
import Navigation from '../../Components/Navigation/Navigation';
import { API_BASE_URL } from '../../config/config';
import './SendEmails.css';

function SendEmails() {
  const [user, setUser] = useState(null);
  const [sending, setSending] = useState(false);
  const [quota, setQuota] = useState({ remaining: null, limit: null, loading: true });
  const [formValues, setFormValues] = useState({
    subject: '',
    message: '',
  });
  // const [attachments, setAttachments] = useState([]); // Removed file upload functionality
  const [statusMessage, setStatusMessage] = useState(null);
  const [labelName, setLabelName] = useState('');
  const [labelCreating, setLabelCreating] = useState(false);
  const [labelMessage, setLabelMessage] = useState(null);
  const [labelOptions, setLabelOptions] = useState([]); // kept for compatibility (unused visually)
  const [labelsList, setLabelsList] = useState([]); // Full labels data for selected label operations
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [modalLabelName, setModalLabelName] = useState('');
  const [modalSubject, setModalSubject] = useState('');
  const [modalContent, setModalContent] = useState('');
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [editingLabel, setEditingLabel] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [fieldsEditable, setFieldsEditable] = useState(false); // Controls if subject/content can be edited
  const [showSendTypeModal, setShowSendTypeModal] = useState(false); // Modal for local/global selection
  const [showWarningModal, setShowWarningModal] = useState(false); // Modal for language warnings
  const [selectedSendType, setSelectedSendType] = useState(null); // Store selected type for warning
  const [testRecipients, setTestRecipients] = useState(''); // Temporary test recipients input
  const [labelLogs, setLabelLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);

  // immediate click guard for modal save (prevents double POST before state updates)
  const modalSavingRef = React.useRef(false);

  // PAGE lifecycle object (page loading)
  const PAGE = {
    async onLoad() {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      } else {
        window.location.href = '/';
        return;
      }
      try {
        const r = await fetch(`${API_BASE_URL}/emails/labels`);
        const d = r.ok ? await r.json() : { items: [] };
        // Normalize labels so each item consistently has an ID field
        const labels = (d?.items || []).map((x) => ({
          ...x,
          ID: x.ID ?? x.id ?? x.Id ?? null,
        }));
        console.log('Loaded labels:', labels);
        setLabelOptions(labels.map(x => x.label_name || x.Name || x.name).filter(Boolean));
        setLabelsList(labels);
        console.log('Label options set:', labels.map(x => x.label_name || x.Name || x.name).filter(Boolean));
      } catch (error) {
        console.error('Error loading labels:', error);
        setLabelOptions([]);
        setLabelsList([]);
      }
    },
  };

  useEffect(() => {
    PAGE.onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real-time quota via Socket.IO; fallback to polling if socket fails
  useEffect(() => {
    let cancelled = false;
    let timer;
    let socket;
    function setData(d){ if (!cancelled) setQuota({ remaining: d?.remaining ?? null, limit: d?.limit ?? null, loading: false }); }
    try {
      const socketOrigin = (() => {
        try {
          const u = new URL(API_BASE_URL);
          return u.origin;
        } catch (e) {
          return window.location.origin;
        }
      })();
      socket = io(socketOrigin, { path: '/socket.io' });
      socket.on('connect', () => {
        // mark loading false on first connect
        setData({ remaining: quota.remaining, limit: quota.limit });
      });
      socket.on('quota', (payload) => setData(payload));
      socket.on('connect_error', () => { /* ignore */ });
    } catch (e) {
      // ignore
    }
    // fallback polling
    async function loadQuota() {
      try {
        const r = await fetch(`${API_BASE_URL}/emails/quota`);
        const d = r.ok ? await r.json() : { remaining: null, limit: null };
        setData(d);
      } catch (e) {
        setData({ remaining: null, limit: null });
      }
    }
    loadQuota();
    timer = setInterval(loadQuota, 120000);
    return () => { cancelled = true; if (timer) clearInterval(timer); if (socket) socket.close(); };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };


  const openLabelModal = () => {
    setModalLabelName('');
    setModalSubject('');
    setModalContent('');
    setModalError('');
    setShowLabelModal(true);
  };

  const closeLabelModal = () => {
    setShowLabelModal(false);
    setModalError('');
  };

  const openEditModal = (label) => {
    setEditingLabel(label);
    setModalLabelName(label.label_name);
    setModalSubject(label.mail_subject || '');
    setModalContent(label.mail_content || '');
    setModalError('');
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingLabel(null);
    setModalError('');
  };

  const deleteLabel = async (labelId) => {
    if (!window.confirm('Are you sure you want to delete this label? This will also delete all associated logs.')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_BASE_URL}/emails/labels/${labelId}`, {
        method: 'DELETE'
      });
      
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Request failed with status ${res.status}`);
      }
      
      // Clear form if deleted label was selected
      const deletedLabel = labelsList.find(label => label.ID === labelId);
      if (deletedLabel && labelName === deletedLabel.label_name) {
        setLabelName('');
        setFormValues({ subject: '', message: '' });
      }
      
      // Refresh labels list
      await PAGE.onLoad();
      setLabelMessage({ type: 'success', text: 'Label deleted successfully' });
    } catch (error) {
      console.error('Delete error:', error);
      setLabelMessage({ type: 'danger', text: error.message });
    }
  };

  const updateLabel = async () => {
    // Enhanced guard against double submission
    if (modalSavingRef.current || modalSaving) {
      console.log('Update blocked - already in progress');
      return;
    }
    
    const name = modalLabelName.trim();
    if (!name) {
      setModalError('Label name is required.');
      return;
    }
    
    // Set guards immediately
    modalSavingRef.current = true;
    setModalSaving(true);
    setModalError('');
    
    try {
      const requestData = { 
        label_name: name, 
        subject: modalSubject.trim(), 
        body: modalContent.trim() 
      };
      
      console.log('=== UPDATE LABEL DEBUG ===');
      console.log('Updating label ID:', editingLabel.ID);
      console.log('Sending data:', requestData);
      
      const res = await fetch(`${API_BASE_URL}/emails/labels/${editingLabel.ID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });
      
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = err.error || `Request failed with status ${res.status}`;
        const code = err.code || '';
        // Friendlier, specific messages
        if (code === 'DUPLICATE_LABEL') {
          throw new Error('A label with this name already exists. Please choose another name.');
        }
        if (code === 'DB_UNAVAILABLE') {
          throw new Error('Database is temporarily unavailable. Your changes were NOT saved.');
        }
        throw new Error(msg);
      }
      
      const result = await res.json();
      console.log('Update successful:', result);
      
      // Update the form values with the new data if this label is currently selected
      if (editingLabel && labelName === editingLabel.label_name) {
        setFormValues({ 
          subject: modalSubject.trim(), 
          message: modalContent.trim() 
        });
        setFieldsEditable(true); // Enable editing after label update
        console.log('Updated form values for selected label');
      }
      
      closeEditModal();
      await PAGE.onLoad();
      setLabelMessage({ type: 'success', text: 'Label updated successfully' });
      
    } catch (error) {
      console.error('Update error:', error);
      setModalError(error.message || 'Failed to update label.');
    } finally {
      // Always reset guards
      modalSavingRef.current = false;
      setModalSaving(false);
    }
  };

  const saveLabelFromModal = async () => {
    // Enhanced guard against double submission
    if (modalSavingRef.current || modalSaving) {
      console.log('Save blocked - already in progress');
      return;
    }
    
    const name = modalLabelName.trim();
    if (!name) {
      setModalError('Label name is required.');
      return;
    }
    
    // Additional check: disable the modal buttons immediately
    const saveButton = document.querySelector('button[onclick*="save-label"]');
    if (saveButton) {
      saveButton.disabled = true;
      saveButton.textContent = 'Saving...';
    }
    
    // Set guards immediately
    modalSavingRef.current = true;
    setModalSaving(true);
    setModalError('');
    
    try {
      const requestData = { 
        label_name: name, 
        subject: modalSubject.trim(), 
        body: modalContent.trim() 
      };
      
      console.log('=== FRONTEND DEBUG ===');
      console.log('Sending data:', requestData);
      console.log('Modal values:');
      console.log('- Name:', name);
      console.log('- Subject:', modalSubject);
      console.log('- Content:', modalContent);
      
      const res = await fetch(`${API_BASE_URL}/emails/labels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });
      
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Request failed with status ${res.status}`);
      }
      
      const result = await res.json();
      console.log('Save successful:', result);
      
      // Set the new label as selected and load its template
      setLabelName(name);
      setFormValues({ 
        subject: modalSubject.trim(), 
        message: modalContent.trim() 
      });
      setFieldsEditable(true); // Enable editing for newly created label
      console.log('New label selected and form populated');
      
      setLabelMessage({ type: 'success', text: 'Label created successfully' });
      setShowLabelModal(false);
      await PAGE.onLoad();
      
    } catch (err) {
      console.error('Save error:', err);
      setModalError(err.message || 'Failed to create label.');
    } finally {
      // Always reset guards and re-enable buttons
      setModalSaving(false);
      modalSavingRef.current = false;
      
      // Re-enable the save button
      const saveButton = document.querySelector('button[onclick*="save-label"]');
      if (saveButton) {
        saveButton.disabled = false;
        saveButton.textContent = 'Save Label';
      }
    }
  };

  const isValid = () => formValues.subject.trim() && formValues.message.trim();

  // const filesToAttachmentPayload = async (files) => {
  //   const map = files.map((file) =>
  //     new Promise((resolve, reject) => {
  //       const reader = new FileReader();
  //       reader.onload = () => {
  //         const base64 = String(reader.result).split(',')[1] || '';
  //         resolve({ filename: file.name, content: base64, encoding: 'base64' });
  //       };
  //       reader.onerror = reject;
  //       reader.readAsDataURL(file);
  //     })
  //   );
  //   return Promise.all(map);
  // }; // Removed file upload functionality

  const sendEmail = async () => {
    if (!isValid()) return;
    if (quota && quota.remaining != null && Number(quota.remaining) <= 0) {
      setStatusMessage({ type: 'warning', text: 'Daily quota exhausted. Please try again later.' });
      return;
    }
    setShowSendTypeModal(true);
  };

  // Temporary: direct test send only to provided recipients
  const sendEmailTest = async () => {
    if (sending) {
      console.log('Already sending, ignoring duplicate test request');
      return;
    }
    const trimmed = String(testRecipients || '').trim();
    if (!isValid() || !trimmed) {
      setStatusMessage({ type: 'danger', text: 'Subject, message ve test alıcıları zorunludur.' });
      return;
    }
    setSending(true);
    setStatusMessage(null);
    try {
      const payload = {
        to: 'dummy@example.com',
        subject: formValues.subject,
        message: formValues.message,
        labelName: labelName,
        createdUserID: user?.id || user?.ID || null,
        attachments: [],
        sendType: 'local',
        testRecipients: trimmed,
      };
      const res = await fetch(`${API_BASE_URL}/emails/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Request failed with status ${res.status}`);
      }
      const data = await res.json();
      const sentCount = (data?.results || []).filter(x => x.status === 'SENT').length;
      const failed = (data?.results || []).filter(x => x.status === 'FAILED').length;
      const totalRecipients = data?.totalRecipients || 0;
      setStatusMessage({ type: 'success', text: `TEST: Sent to ${sentCount}/${totalRecipients} recipients (${failed} failed)` });
    } catch (err) {
      setStatusMessage({ type: 'danger', text: err.message || 'An error occurred while sending.' });
    } finally {
      setSending(false);
    }
  };

  const handleSendTypeSelection = (sendType) => {
    setSelectedSendType(sendType);
    setShowSendTypeModal(false);
    setShowWarningModal(true);
  };

  const cancelSend = () => {
    setShowWarningModal(false);
    setSelectedSendType(null);
    setShowSendTypeModal(true);
  };

  const proceedWithSend = async () => {
    if (sending) {
      console.log('Already sending, ignoring duplicate request');
      return;
    }
    setShowWarningModal(false);
    setSending(true);
    setStatusMessage(null);
    try {
      // const attachmentPayload = await filesToAttachmentPayload(attachments); // Removed file upload

      const payload = {
        to: 'dummy@example.com', // Backend will determine recipients based on sendType
        subject: formValues.subject,
        message: formValues.message,
        labelName: labelName,
        createdUserID: user?.id || user?.ID || null,
        attachments: [], // Removed file upload functionality
        sendType: selectedSendType, // 'local' or 'global'
      };

      const res = await fetch(`${API_BASE_URL}/emails/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Request failed with status ${res.status}`);
      }
      const data = await res.json();
      
      console.log('Email sending response:', data);
      
      const sentCount = (data?.results || []).filter(x => x.status === 'SENT').length;
      const failed = (data?.results || []).filter(x => x.status === 'FAILED').length;
      const sendType = data?.sendType || 'unknown';
      const totalRecipients = data?.totalRecipients || 0;
      const processedRecipients = data?.processedRecipients || 0;
      const quotaExhausted = data?.quotaExhausted || false;
      const quotaExhaustedReason = data?.quotaExhaustedReason || '';
      
      let messageText = '';
      let messageType = 'success';
      
      if (quotaExhausted) {
        messageType = 'warning';
        messageText = `⚠️ ${quotaExhaustedReason} ${sentCount} emails sent successfully out of ${processedRecipients} processed.`;
      } else {
        messageText = `✅ ${sendType.toUpperCase()}: Sent to ${sentCount}/${totalRecipients} recipients (${failed} failed)`;
      }
      
      setStatusMessage({ 
        type: messageType, 
        text: messageText
      });
      setFormValues({ subject: '', message: '' });
      // setAttachments([]); // Removed file upload functionality
    } catch (err) {
      setStatusMessage({ type: 'danger', text: err.message || 'An error occurred while sending.' });
    } finally {
      setSending(false);
    }
  };

  // const clearFormAction = () => {
  //   setFormValues({ subject: '', message: '' });
  //   // setAttachments([]); // Removed file upload functionality
  // }; // Removed clear functionality

  const onLabelChange = async (value) => {
    console.log('=== LABEL CHANGE DEBUG ===');
    console.log('Selected label:', value);
    setLabelName(value);
    
    // If no label selected (default option), clear the form
    if (!value) {
      setFormValues({ subject: '', message: '' });
      setFieldsEditable(false); // Disable editing when no label selected
      setLabelLogs([]);
      console.log('Form cleared - no label selected');
      return;
    }
    
    setFieldsEditable(false); // Disable editing when label is selected (read-only mode)
    
    try {
      const url = `${API_BASE_URL}/emails/labels/${encodeURIComponent(value)}/template`;
      console.log('Fetching template from:', url);
      const res = await fetch(url);
      if (!res.ok) {
        console.log('Template not found or error:', res.status);
        return; // silent if not found
      }
      const d = await res.json();
      console.log('Template data received:', d);
      setFormValues({ subject: d.subject || '', message: d.body || '' });
      console.log('Form values updated:', { subject: d.subject || '', message: d.body || '' });
      // Load logs for selected label
      setLogsLoading(true);
      try {
        const lr = await fetch(`${API_BASE_URL}/emails/labels/${encodeURIComponent(value)}/logs/detailed`);
        const ld = lr.ok ? await lr.json() : { items: [] };
        setLabelLogs(ld.items || []);
      } catch (e) {
        setLabelLogs([]);
      } finally {
        setLogsLoading(false);
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      setLabelLogs([]);
    }
  };

  // Get selected label object
  const getSelectedLabel = () => {
    if (!labelName) return null;
    return labelsList.find(label => label.label_name === labelName);
  };

  // Edit selected label
  const editSelectedLabel = () => {
    const selectedLabel = getSelectedLabel();
    if (!selectedLabel) return;
    if (!selectedLabel.ID) {
      console.warn('Selected label has no ID and cannot be edited (likely in-memory only).', selectedLabel);
      setLabelMessage({
        type: 'warning',
        text: 'This label cannot be edited because it has no database ID. Please ensure the database is online and recreate the label if needed.'
      });
      return;
    }
    openEditModal(selectedLabel);
  };

  // Delete selected label
  const deleteSelectedLabel = () => {
    const selectedLabel = getSelectedLabel();
    if (!selectedLabel) return;
    if (!selectedLabel.ID) {
      console.warn('Selected label has no ID and cannot be deleted (likely in-memory only).', selectedLabel);
      setLabelMessage({
        type: 'warning',
        text: 'This label cannot be deleted because it has no database ID. Please ensure the database is online and recreate the label if needed.'
      });
      return;
    }
    deleteLabel(selectedLabel.ID);
  };

  // elementFunctionObject-style action dispatcher
  const elementFunctionObject = {
    'send-email': sendEmail,
    // 'clear-form': clearFormAction, // Removed clear functionality
    'open-label-modal': openLabelModal,
    'close-label-modal': closeLabelModal,
    'save-label': saveLabelFromModal,
  };

  const dispatchAction = async (key) => {
    const fn = elementFunctionObject[key];
    if (typeof fn === 'function') {
      await fn();
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Navigation user={user} onLogout={handleLogout} currentPage="journal" />
      <Container fluid className="mt-4 sendemails-container">
        <Row className="mb-3 align-items-center">
          <Col>
            <h2 className="page-title"><FaEnvelope className="me-2" />Send Emails</h2>
            <p className="text-muted">Send individual emails to academicians</p>
          </Col>
          
        </Row>

        <Row>
          <Col lg={8}>
            <Card className="mb-4">
              <Card.Body>
                <Form onSubmit={(e) => { e.preventDefault(); dispatchAction('send-email'); }}>
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Text className="text-muted">Recipients will be loaded from the academicians table automatically. From/SMTP are configured on the server.</Form.Text>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3" controlId="formSubject">
                    <Form.Label>Subject</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="subject" 
                      placeholder="Subject" 
                      value={formValues.subject} 
                      onChange={handleChange}
                      readOnly={!fieldsEditable}
                      style={{ backgroundColor: fieldsEditable ? 'white' : '#f8f9fa' }}
                    />
                    {!fieldsEditable && labelName && (
                      <Form.Text className="text-muted">
                        <small>Subject is read-only. Edit the label to modify this field.</small>
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="formMessage">
                    <Form.Label>Message</Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows={8} 
                      name="message" 
                      placeholder="Write your message..." 
                      value={formValues.message} 
                      onChange={handleChange}
                      readOnly={!fieldsEditable}
                      style={{ backgroundColor: fieldsEditable ? 'white' : '#f8f9fa' }}
                    />
                    {!fieldsEditable && labelName && (
                      <Form.Text className="text-muted">
                        <small>Message is read-only. Edit the label to modify this field.</small>
                      </Form.Text>
                    )}
                  </Form.Group>


                  {/* File upload functionality removed */}

                  <div className="d-flex gap-2">
                    <Button type="submit" variant="primary" disabled={!isValid() || sending || (quota && quota.remaining != null && Number(quota.remaining) <= 0)}>
                      {sending ? (
                        <>
                          <Spinner size="sm" animation="border" className="me-2" />
                          Sending...
                        </>
                      ) : (
                        'Send Email'
                      )}
                    </Button>

                  
                  </div>

                  {(quota && quota.remaining != null && Number(quota.remaining) <= 0) && (
                    <div className="alert alert-warning mt-3 mb-0" role="alert">
                      Daily Brevo quota is exhausted. Sending is disabled until quota refreshes.
                    </div>
                  )}

                  {statusMessage && (
                    <div className={`alert alert-${statusMessage.type} mt-3 mb-0`} role="alert">
                      {statusMessage.text}
                    </div>
                  )}
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">Create New Label</h5>
                <div className="mb-3 d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Labels</h6>
                  <Button type="button" size="sm" variant="outline-primary" onClick={() => dispatchAction('open-label-modal')}>Create New Label</Button>
                </div>
                <Form.Group className="mb-3" controlId="formLabelsCombo">
                  <Form.Label className="small text-muted">Available Labels</Form.Label>
                  <Form.Select value={labelName} onChange={(e) => onLabelChange(e.target.value)} disabled={!labelOptions.length}>
                    <option value="">{labelOptions.length ? 'Select label' : 'No labels found'}</option>
                    {labelOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                
                {/* Selected Label Actions */}
                {labelName && (
                  <div className="mb-3">
                    <div className="d-flex gap-2">
                      <Button 
                        type="button" 
                        size="sm" 
                        variant="outline-warning" 
                        onClick={editSelectedLabel}
                        className="flex-fill"
                      >
                        Edit Selected Label
                      </Button>
                      <Button 
                        type="button" 
                        size="sm" 
                        variant="outline-danger" 
                        onClick={deleteSelectedLabel}
                        className="flex-fill"
                      >
                        Delete Selected Label
                      </Button>
                    </div>
                    <small className="text-muted">
                      Selected: <strong>{labelName}</strong>
                    </small>
                  </div>
                )}
                {/* Label creation now handled via modal only; latest label shown above */}
                {labelMessage && (
                  <div className={`alert alert-${labelMessage.type} mt-3 mb-0`} role="alert">
                    {labelMessage.text}
                  </div>
                )}
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Delivery Logs</h5>
                  <Button size="sm" variant="outline-secondary" onClick={() => setShowLogsModal(true)} disabled={!labelName}>
                    View Full Logs
                  </Button>
                </div>
                {logsLoading ? (
                  <div className="d-flex align-items-center gap-2">
                    <Spinner size="sm" animation="border" />
                    <span className="small text-muted">Loading logs...</span>
                  </div>
                ) : labelLogs.length === 0 ? (
                  <div className="small text-muted">No logs for this label yet.</div>
                ) : (
                  <div className="table-responsive" style={{ maxHeight: 320, overflowY: 'auto' }}>
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead>
                        <tr>
                          <th scope="col">Recipient</th>
                          <th scope="col">Status</th>
                          <th scope="col">Date</th>
                          <th scope="col">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {labelLogs.map((row) => (
                          <tr key={row.ID}>
                            <td><span className="small">{row.recipientEmail || row.sentUserID || '-'}</span></td>
                            <td>
                              <Badge bg={String(row.status).toUpperCase() === 'SENT' ? 'success' : 'secondary'}>
                                {String(row.status).toUpperCase()}
                              </Badge>
                            </td>
                            <td><span className="small">{row.sentDate ? String(row.sentDate).split('T')[0] : '-'}</span></td>
                            <td><span className="small">{row.sendTime ?? '-'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <LabelCreateModal
        show={showLabelModal}
        onHide={() => dispatchAction('close-label-modal')}
        name={modalLabelName}
        setName={setModalLabelName}
        subject={modalSubject}
        setSubject={setModalSubject}
        content={modalContent}
        setContent={setModalContent}
        saving={modalSaving}
        error={modalError}
        onSave={() => dispatchAction('save-label')}
      />
      <LabelEditModal
        show={showEditModal}
        onHide={closeEditModal}
        name={modalLabelName}
        setName={setModalLabelName}
        subject={modalSubject}
        setSubject={setModalSubject}
        content={modalContent}
        setContent={setModalContent}
        saving={modalSaving}
        error={modalError}
        onSave={updateLabel}
      />
      <SendTypeModal
        show={showSendTypeModal}
        onHide={() => setShowSendTypeModal(false)}
        onSelectType={handleSendTypeSelection}
      />
      <WarningModal
        show={showWarningModal}
        onHide={cancelSend}
        onProceed={proceedWithSend}
        sendType={selectedSendType}
      />

      {/* Logs Modal */}
      <Modal show={showLogsModal} onHide={() => setShowLogsModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Delivery Logs {labelName ? `- ${labelName}` : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {logsLoading ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner size="sm" animation="border" />
              <span className="small text-muted">Loading logs...</span>
            </div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <table className="table table-sm table-striped align-middle mb-0">
                <thead className="table-light position-sticky top-0" style={{ zIndex: 1 }}>
                  <tr>
                    <th>ID</th>
                    <th>Recipient</th>
                    <th>Name</th>
                    <th>Region</th>
                    <th>Country</th>
                    <th>University</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Sent Date</th>
                    <th>Sent Time</th>
                  </tr>
                </thead>
                <tbody>
                  {labelLogs.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center text-muted small">No records</td>
                    </tr>
                  ) : (
                    labelLogs.map(row => (
                      <tr key={row.ID}>
                        <td>{row.ID}</td>
                        <td className="small">{row.email || '-'}</td>
                        <td className="small">{row.name || '-'}</td>
                        <td className="small">{row.region ?? '-'}</td>
                        <td className="small">{row.country || '-'}</td>
                        <td className="small">{row.university || '-'}</td>
                        <td className="small">{row.department || '-'}</td>
                        <td>
                          <Badge bg={String(row.status).toUpperCase() === 'SENT' ? 'success' : 'secondary'}>
                            {String(row.status).toUpperCase()}
                          </Badge>
                        </td>
                        <td className="small">{row.sentDate ? String(row.sentDate).split('T')[0] : '-'}</td>
                        <td className="small">{row.sendTime ?? '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLogsModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default SendEmails;

// Modal for selecting send type (local/global)
function SendTypeModal({ show, onHide, onSelectType }) {
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Select Send Type</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-4">How would you like to send this email?</p>
        <div className="d-flex gap-3">
          <Button 
            variant="primary" 
            size="lg"
            onClick={() => onSelectType('local')}
            className="flex-fill"
          >
            Send Local
          </Button>
          <Button 
            variant="success" 
            size="lg"
            onClick={() => onSelectType('global')}
            className="flex-fill"
          >
            Send Global
          </Button>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// Modal for language warnings
function WarningModal({ show, onHide, onProceed, sendType }) {
  const getWarningContent = () => {
    if (sendType === 'local') {
      return {
        title: 'Local Email Warning',
        message: 'This email will be sent to local academicians. Therefore, you can send it in the local language.',
        icon: '🏠'
      };
    } else if (sendType === 'global') {
      return {
        title: 'Global Email Warning',
        message: 'This email will be sent to international academicians who work on global collaborations. Therefore, it would be more appropriate to choose a universal language.',
        icon: '🌍'
      };
    }
    return { title: '', message: '', icon: '' };
  };

  const { title, message, icon } = getWarningContent();

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{icon} {title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-4">{message}</p>
        <div className="alert alert-info">
          <strong>Note:</strong> Please ensure your email content is appropriate for the selected audience.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onProceed}>
          Next
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// Modal for creating a new label
// Placed at the end to keep JSX simple above
function LabelCreateModal({ show, onHide, name, setName, subject, setSubject, content, setContent, saving, error, onSave }) {
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create New Label</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group controlId="modalLabelName" className="mb-3">
          <Form.Label>Label Name *</Form.Label>
          <Form.Control 
            type="text" 
            placeholder="e.g., issue5456" 
            value={name} 
            onChange={(e) => setName(e.target.value)} 
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); } }} 
          />
        </Form.Group>
        
        <Form.Group controlId="modalSubject" className="mb-3">
          <Form.Label>Email Subject</Form.Label>
          <Form.Control 
            type="text" 
            placeholder="e.g., Issue Report - {issue_number}" 
            value={subject} 
            onChange={(e) => setSubject(e.target.value)} 
          />
        </Form.Group>
        
        <Form.Group controlId="modalContent" className="mb-3">
          <Form.Label>Email Content</Form.Label>
          <Form.Control 
            as="textarea" 
            rows={6}
            placeholder="Enter your email template content here..." 
            value={content} 
            onChange={(e) => setContent(e.target.value)} 
            className="mail-content-justify"
          />
        </Form.Group>
        
        {error ? <div className="alert alert-danger mt-3 mb-0">{error}</div> : null}
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button type="button" variant="primary" onClick={onSave} disabled={saving || !name.trim()}>
          {saving ? (<><Spinner size="sm" animation="border" className="me-2" />Saving...</>) : 'Save Label'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// Modal for editing an existing label
function LabelEditModal({ show, onHide, name, setName, subject, setSubject, content, setContent, saving, error, onSave }) {
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Label</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group controlId="editModalLabelName" className="mb-3">
          <Form.Label>Label Name *</Form.Label>
          <Form.Control 
            type="text" 
            placeholder="e.g., issue5456" 
            value={name} 
            onChange={(e) => setName(e.target.value)} 
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); } }} 
          />
        </Form.Group>
        
        <Form.Group controlId="editModalSubject" className="mb-3">
          <Form.Label>Email Subject</Form.Label>
          <Form.Control 
            type="text" 
            placeholder="e.g., Issue Report - {issue_number}" 
            value={subject} 
            onChange={(e) => setSubject(e.target.value)} 
          />
        </Form.Group>
        
        <Form.Group controlId="editModalContent" className="mb-3">
          <Form.Label>Email Content</Form.Label>
          <Form.Control 
            as="textarea" 
            rows={6}
            placeholder="Enter your email template content here..." 
            value={content} 
            onChange={(e) => setContent(e.target.value)} 
            className="mail-content-justify"
          />
        </Form.Group>
        
        {error ? <div className="alert alert-danger mt-3 mb-0">{error}</div> : null}
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button type="button" variant="primary" onClick={onSave} disabled={saving || !name.trim()}>
          {saving ? (<><Spinner size="sm" animation="border" className="me-2" />Updating...</>) : 'Update Label'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}